const _excluded = ["arrows", "hasArrows", "dots", "hasDots", "nextButtonEl", "prevButtonEl", "dotsEl"],
  _excluded2 = ["testid", "containerElement", "className", "hasArrows", "arrows", "hasDots", "dots", "scrollToSlide", "scrollToPage", "skipTrack", "children"];
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
function _defineProperty(e, r, t) { return (r = _toPropertyKey(r)) in e ? Object.defineProperty(e, r, { value: t, enumerable: !0, configurable: !0, writable: !0 }) : e[r] = t, e; }
function _toPropertyKey(t) { var i = _toPrimitive(t, "string"); return "symbol" == typeof i ? i : i + ""; }
function _toPrimitive(t, r) { if ("object" != typeof t || !t) return t; var e = t[Symbol.toPrimitive]; if (void 0 !== e) { var i = e.call(t, r || "default"); if ("object" != typeof i) return i; throw new TypeError("@@toPrimitive must return a primitive value."); } return ("string" === r ? String : Number)(t); }
function _objectWithoutProperties(e, t) { if (null == e) return {}; var o, r, i = _objectWithoutPropertiesLoose(e, t); if (Object.getOwnPropertySymbols) { var n = Object.getOwnPropertySymbols(e); for (r = 0; r < n.length; r++) o = n[r], t.indexOf(o) >= 0 || {}.propertyIsEnumerable.call(e, o) && (i[o] = e[o]); } return i; }
function _objectWithoutPropertiesLoose(r, e) { if (null == r) return {}; var t = {}; for (var n in r) if ({}.hasOwnProperty.call(r, n)) { if (e.indexOf(n) >= 0) continue; t[n] = r[n]; } return t; }
import React, { useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import GliderJS from 'glider-js';
import { WideArrowBackward, WideArrowForward } from "../../icons";
import { generateUniqueId } from "../../utils/generateUniqueId";
import { IconButton } from "../IconButton";
import 'glider-js/glider.min.css';
import styles from "./Glider.module.scss";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
const makeGliderOptions = _ref => {
  let {
      arrows,
      hasArrows,
      dots,
      hasDots,
      nextButtonEl,
      prevButtonEl,
      dotsEl
    } = _ref,
    restProps = _objectWithoutProperties(_ref, _excluded);
  return _objectSpread(_objectSpread({}, restProps), {}, {
    skipTrack: true,
    arrows: hasArrows && {
      next: arrows && arrows.next || nextButtonEl,
      prev: arrows && arrows.prev || prevButtonEl
    } || undefined,
    dots: hasDots && dots || dotsEl || undefined
  });
};
export default /*#__PURE__*/forwardRef((props, ref) => {
  const {
      testid = 'carousel',
      containerElement,
      className,
      hasArrows,
      arrows,
      hasDots,
      dots,
      scrollToSlide,
      scrollToPage,
      skipTrack,
      children
    } = props,
    restProps = _objectWithoutProperties(props, _excluded2);
  const autoId = generateUniqueId('glider');
  const prevButtonRef = useRef(null);
  const nextButtonRef = useRef(null);
  const dotsRef = useRef(null);
  const elementRef = useRef(null);
  const gliderRef = useRef(null);

  // initialize the glider
  const callbackRef = useCallback(element => {
    elementRef.current = element;
    if (element && !gliderRef.current) {
      const glider = new GliderJS(element, makeGliderOptions(_objectSpread(_objectSpread({}, restProps), {}, {
        arrows,
        hasArrows,
        dots,
        hasDots,
        nextButtonEl: nextButtonRef.current,
        prevButtonEl: prevButtonRef.current,
        dotsEl: dotsRef.current
      })));
      gliderRef.current = glider;
      if (scrollToSlide) {
        glider.scrollItem(scrollToSlide - 1);
      } else if (scrollToPage) {
        glider.scrollItem(scrollToPage - 1, true);
      }
    }
  }, [arrows, dots, hasArrows, hasDots, restProps, scrollToPage, scrollToSlide]);

  // when the props update, sync the glider
  useEffect(() => {
    if (gliderRef.current) {
      gliderRef.current.setOption(makeGliderOptions(_objectSpread(_objectSpread({}, restProps), {}, {
        arrows,
        hasArrows,
        dots,
        hasDots,
        nextButtonEl: nextButtonRef.current,
        prevButtonEl: prevButtonRef.current,
        dotsEl: dotsRef.current
      })), true);
      gliderRef.current.refresh(true);
    }
  }, [arrows, dots, hasArrows, hasDots, restProps]);

  // expose the glider instance to the user so they can call the methods too
  useImperativeHandle(ref, () => gliderRef.current);
  const Element = containerElement || 'div';
  return /*#__PURE__*/_jsxs(Element, {
    className: styles['glider-contain'],
    children: [props.hasArrows && !arrows && /*#__PURE__*/_jsx(IconButton, {
      skin: "primaryOpaque",
      className: styles['glider-prev'],
      "data-testid": "".concat(testid, "-previous-btn"),
      ref: prevButtonRef,
      children: /*#__PURE__*/_jsx(WideArrowBackward, {})
    }), /*#__PURE__*/_jsx("div", {
      id: testid || autoId,
      className: className,
      ref: callbackRef,
      children: skipTrack ? children : /*#__PURE__*/_jsx("div", {
        children: children
      })
    }), hasDots && !dots && /*#__PURE__*/_jsx("div", {
      ref: dotsRef
    }), props.hasArrows && !arrows && /*#__PURE__*/_jsx(IconButton, {
      skin: "primaryOpaque",
      className: styles['glider-next'],
      "data-testid": "".concat(testid, "-next-btn"),
      ref: nextButtonRef,
      children: /*#__PURE__*/_jsx(WideArrowForward, {})
    })]
  });
});